import { createContext, useContext } from 'react';
import './ShortcutsOverlay.css';

export interface ShortcutGroup {
  title: string;
  shortcuts: { keys: string[]; description: string }[];
}

export const ShortcutsContext = createContext<ShortcutGroup[]>([]);

const isMac = typeof navigator != 'undefined' && /Mac|iPhone|iPad|iPod/.test(navigator.platform);

const KEY_LABELS: Record<string, string> = isMac
  ? { 'Alt': '⌥', 'Enter': 'Return', 'Shift': '⇧', 'Esc': 'Esc' }
  : {};

function keyLabel(key: string): string {
  return KEY_LABELS[key] ?? key;
}

const GLOBAL_SHORTCUTS: ShortcutGroup = {
  title: 'Navigation',
  shortcuts: [
    { keys: ['↑', '↓'], description: 'Select prev / next element' },
    { keys: ['←', '→'], description: 'Select left / right element' },
    { keys: ['Enter'], description: 'Select / activate' },
    { keys: ['Esc'], description: 'Leave / show menu' },
    { keys: ['Alt', 'm'], description: 'Focus menu (sidebar)' },
  ],
};

const ShortcutsOverlay: React.FC<{ show: boolean }> = ({ show }) => {
  const pageShortcuts = useContext(ShortcutsContext);

  if (!show) return null;

  // Merge page groups into global groups by matching title
  const allGroups: ShortcutGroup[] = [{ ...GLOBAL_SHORTCUTS, shortcuts: [...GLOBAL_SHORTCUTS.shortcuts] }];
  for (const group of pageShortcuts) {
    const existing = allGroups.find(g => g.title == group.title);
    if (existing) existing.shortcuts.push(...group.shortcuts);
    else allGroups.push(group);
  }

  return (
    <div className="shortcutsOverlay">
      <div className="shortcutsCard">
        <div className="shortcutsTitle">Keyboard Shortcuts</div>
        <div className="shortcutsGroups">
          {allGroups.map(group => (
            <div key={group.title} className="shortcutsGroup">
              <div className="shortcutsGroupTitle">{group.title}</div>
              {group.shortcuts.map((sc, i) => (
                <div key={i} className="shortcutRow">
                  <span className="shortcutKeys">
                    {sc.keys.map((k, j) => <kbd key={j}>{keyLabel(k)}</kbd>)}
                  </span>
                  <span className="shortcutDesc">{sc.description}</span>
                </div>
              ))}
            </div>
          ))}
        </div>
        <div className="shortcutsHint">Press Esc or ? to dismiss</div>
      </div>
    </div>
  );
};

export default ShortcutsOverlay;
